<script setup lang="ts">
    import { InputRow, Combobox, MultiCombobox } from '@/components/ui/form';
    import { computed, watch } from 'vue';
    import { useI18n } from 'vue-i18n';
    import { today, getLocalTimeZone } from '@internationalized/date';
    import { useSalaryEmployeeData } from '@/composables/useSalaryEmployeeData';

    const props = defineProps<{
        form: any;
        selectedCompanyId: number;
    }>();

    const { t, locale, messages } = useI18n();

    const { employeesOptions, isPendingEmployeesOptions } = useSalaryEmployeeData(
        props.selectedCompanyId,
    );

    const typeOptions = computed(() => {
        const types = (messages.value[locale.value] as any).salary.excel.types;

        return Object.entries(types).map(([key, label]) => ({
            value: key,
            label: label as string,
        }));
    });

    const monthOptions = computed(() => {
        const months = (messages.value[locale.value] as any).salary.sheets.months;

        return Object.entries(months).map(([key, label]) => ({
            value: Number(key),
            label: label as string,
        }));
    });

    const yearOptions = computed(() => {
        const currentYear = today(getLocalTimeZone()).year;

        return Array.from({ length: 2 }, (_, i) => currentYear - i).map((year) => ({
            value: year,
            label: year.toString(),
        }));
    });
</script>

<template>
    <div class="grid gap-3 sm:grid-cols-1">
        <InputRow
            :form="form"
            name="type"
            label="salary.payroll.type"
        >
            <Combobox
                v-model="form.type"
                :options="typeOptions"
                :placeholder="t('salary.payroll.type')"
                :show-remove-button="false"
            />
        </InputRow>
        <InputRow
            :form="form"
            name="month"
            label="salary.payroll.month"
        >
            <Combobox
                v-model="form.month"
                :options="monthOptions"
                :placeholder="t('salary.payroll.month')"
                :show-remove-button="false"
            />
        </InputRow>
        <InputRow
            :form="form"
            name="year"
            label="salary.payroll.year"
        >
            <Combobox
                v-model="form.year"
                :options="yearOptions"
                :placeholder="t('salary.payroll.year')"
                :show-remove-button="false"
            />
        </InputRow>
        <InputRow
            :form="form"
            name="employeeIds"
            label="salary.payroll.employees"
            v-if="form.type === 'extra'"
        >
            <MultiCombobox
                v-model="form.employeeIds"
                :options="employeesOptions"
                :placeholder="t('salary.payroll.employees')"
                :loading="isPendingEmployeesOptions"
                label-by="full_name"
                value-by="id"
            />
        </InputRow>
    </div>
</template>
